import React, { useCallback, useContext, useState } from "react";
import { useFormik } from "formik";
import { UserDropdown } from "components/UserDropdown";

import { DatePicker } from "components/DatePicker";
import { MultilineTextField } from "components/MultilineTextField";

import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UsersContext } from "contexts/UsersContext";

import { styled } from "@mui/material/styles";
import { AxiosContext } from "contexts/AxiosContext";
import { Dialog } from "components/Dialog";

import * as yup from "yup";
import { TextField } from "components/TextField";
import { AxiosResponse } from "axios";
import {
  IProject,
  IUpdateProject,
  ProjectStatuses,
  ProjectTypes
} from "interfaces/project";
import { API_URL_PROJECTS } from "constants/api_urls";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "react-use";
import { dateFormat } from "core/datetime";
import { getCheckProjectNameExists } from "pages/Projects/getCheckProjectNameExists";
import { Dropdown } from "components/Dropdown";
import { MenuItem } from "@mui/material";
import { UNASSIGNED } from "core/constants";

interface EditProjectDialogProps {
  isOpen: boolean;
  selectedProject: IProject;
  onClose: () => void;
  onConfirm: (project: IProject) => void;
}

const PROJECT_NAME_MAX_LENGTH = 500;

const ERROR_MESSAGE_PROJECT_NAME_EXISTS =
  "A project with the same name already exists.";

const PROJECT_DESCRIPTION_MAX_LENGTH = 2000;

const validationSchema = yup.object({
  name: yup
    .string()
    .nullable()
    .required("Project name is required")
    .trim()
    .min(3, "Project name must be at least 3 characters")
    .max(
      PROJECT_NAME_MAX_LENGTH,
      `Project name must be less than ${PROJECT_NAME_MAX_LENGTH} characters`
    )
    .matches(
      /^[a-zA-Z0-9\-\s]+$/,
      "Only alphanumeric characters, dashes and spaces allowed"
    ),
  projectStatus: yup
    .string()
    .label("Status")
    .required(),
  dueDate: yup
    .date()
    .label("Due Date")
    .nullable(),
  projectOwnerId: yup
    .number()
    .min(0)
    .nullable(),
  projectDefaultActionAssgineeId: yup
    .number()
    .label("Default action assignee")
    .min(0)
    .nullable(),
  description: yup
    .string()
    .trim()
    .label("Description")
    .max(
      PROJECT_DESCRIPTION_MAX_LENGTH,
      `Project description must be less than ${PROJECT_DESCRIPTION_MAX_LENGTH} characters`
    )
});

export const EditProjectDialog = ({
  isOpen,
  onClose,
  onConfirm,
  selectedProject
}: EditProjectDialogProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { nonServiceAccountUsers, internalUsers } = useContext(UsersContext);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      name: selectedProject?.name ?? "",
      projectStatus: selectedProject?.project_status,
      projectType: selectedProject?.project_type,
      projectOwnerId: selectedProject?.owner ?? null,
      projectDefaultActionAssigneeId:
        selectedProject?.default_action_assignee ?? null,
      dueDate: selectedProject?.target_completion_date ?? null,
      description: selectedProject?.description ?? ""
    },
    validationSchema,
    onSubmit: values => {
      handleSubmit(formik.isValid && !formHasErrors, values);
    },
    enableReinitialize: true
  });

  const [debouncedProjectName, setDebouncedProjectName] = useState("");

  const checkProjectExists = useQuery<boolean>({
    queryKey: ["project-name-exists", debouncedProjectName],
    queryFn: () => getCheckProjectNameExists(axios, debouncedProjectName),
    cacheTime: 0,
    enabled: isOpen
  });

  const patchEditProject = useMutation(["edit-project"], {
    mutationFn: (updatedProject: IUpdateProject) =>
      axios
        .patch<IUpdateProject, AxiosResponse<IProject>>(
          `${API_URL_PROJECTS}/${selectedProject.id}`,
          updatedProject
        )
        .then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["project", selectedProject.id]
      });
    }
  });

  type FormValuesType = typeof formik.initialValues;

  const handleSubmit = useCallback(
    async (isValid: boolean, values: FormValuesType): Promise<void> => {
      if (!isValid) return;
      try {
        const createProjectResponse = await patchEditProject.mutateAsync({
          name: values.name,
          owner: values.projectOwnerId,
          default_action_assignee: values.projectDefaultActionAssigneeId,
          project_status: values.projectStatus,
          project_type: values.projectType,
          target_completion_date: values.dueDate,
          description: values.description
        });
        addSnackbarToQueue({
          message: "Project was successfully updated.",
          severity: "success"
        });
        onConfirm(createProjectResponse);
      } catch (err) {
        addSnackbarToQueue({
          message: "Error updating project.",
          severity: "error"
        });
      }
    },
    [addSnackbarToQueue, onConfirm, patchEditProject]
  );

  const [,] = useDebounce(
    () => setDebouncedProjectName(formik.values.name),
    200,
    [formik.values.name]
  );

  const loading = patchEditProject.isLoading;

  //Ignore any capitalisation and leading/trailing whitespace differences between the previous name and the name entered
  //This is so that the user can change the capitalisation of the project title
  const nameIsDifferentFromSelected =
    debouncedProjectName.trim().toLowerCase() !==
    selectedProject.name.toLowerCase();

  const formHasErrors =
    !formik.isValid || (checkProjectExists.data && nameIsDifferentFromSelected);

  return (
    <Dialog
      dialogId="dialog-edit-project"
      open={isOpen}
      title={"Edit Project"}
      confirmText="submit"
      size="medium"
      confirmDisabled={loading || formHasErrors}
      onSubmit={e => {
        e.preventDefault();
        formik.submitForm();
      }}
      onClose={() => onClose()}
      aria-label="edit-project-form"
      data-qa="PP-Form-Edit-Project"
      allowClickaway={false}
    >
      <StyledDialogContent>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          name="name"
          required
          id="projectName"
          label="Project Name"
          data-qa="PP-Text-Project-Name"
          inputProps={{ maxLength: PROJECT_NAME_MAX_LENGTH }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.touched.name && Boolean(formik.errors.name)) ||
            (checkProjectExists.data && nameIsDifferentFromSelected)
          }
          helperText={
            (formik.touched.name && formik.errors.name) ||
            (checkProjectExists.data &&
              nameIsDifferentFromSelected &&
              ERROR_MESSAGE_PROJECT_NAME_EXISTS)
          }
          value={formik.values.name}
        />
        <Dropdown
          id="projectStatus"
          name="projectStatus"
          label="Status"
          value={formik.values.projectStatus}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          data-qa="PP-Select-Project-Status"
          error={
            formik.touched.projectStatus && Boolean(formik.errors.projectStatus)
          }
          fullWidth
          required
        >
          {ProjectStatuses.map((status, index) => (
            <MenuItem key={index} value={status}>
              {status}
            </MenuItem>
          )) ?? []}
        </Dropdown>
        <Dropdown
          fullWidth
          id="project-type-select"
          name="projectType"
          label="Project Type"
          required
          value={formik.values.projectType}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.projectType && Boolean(formik.errors.projectType)
          }
          helperText={
            formik.touched.projectType && formik.errors.projectType
              ? formik.errors.projectType
              : undefined
          }
          disabled
        >
          {ProjectTypes.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </Dropdown>
        <UserDropdown
          label="Owner"
          users={internalUsers}
          unassignedValue={UNASSIGNED}
          avatarTooltip="The owner of the project"
          selectedUserId={formik.values.projectOwnerId}
          onChangeSelectedUserId={newOwnerId =>
            formik.setFieldValue("projectOwnerId", newOwnerId)
          }
          error={
            Boolean(formik.touched.projectOwnerId) &&
            Boolean(formik.errors.projectOwnerId)
          }
          helperText={
            formik.touched.projectOwnerId
              ? formik.errors.projectOwnerId
              : undefined
          }
        />
        <UserDropdown
          label={`Default ${formik.values.projectType} Assignee`}
          users={nonServiceAccountUsers}
          avatarTooltip={`The user to be assigned to project ${formik.values.projectType.toLocaleLowerCase()} actions`}
          selectedUserId={formik.values.projectDefaultActionAssigneeId}
          onChangeSelectedUserId={newDefaultActionAssigneeId =>
            formik.setFieldValue(
              "projectDefaultActionAssigneeId",
              newDefaultActionAssigneeId
            )
          }
          error={
            Boolean(formik.touched.projectDefaultActionAssigneeId) &&
            Boolean(formik.errors.projectDefaultActionAssigneeId)
          }
          helperText={
            formik.touched.projectDefaultActionAssigneeId
              ? formik.errors.projectDefaultActionAssigneeId
              : undefined
          }
        />

        <DatePicker
          date={formik.values.dueDate ? new Date(formik.values.dueDate) : null}
          onChange={(ev: string | number | Date) =>
            formik.setFieldValue(
              "dueDate",
              ev ? dateFormat(new Date(ev)) : null
            )
          }
          slotProps={{
            textField: {
              InputLabelProps: {
                shrink: true
              },
              onBlur: formik.handleBlur,
              error: Boolean(formik.errors.dueDate),
              helperText: formik.errors.dueDate
            }
          }}
          id="dueDate"
          label="Due Date"
          data-qa="PP-Text-Due-Date"
          ariaLabel="project-due-date-picker"
          clearable
          disableFuture={false}
          fullWidth
          size="medium"
        />
        <MultilineTextField
          name="description"
          id="projectDescription"
          label="Description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.dirty || formik.touched.description) &&
            Boolean(formik.errors.description)
          }
          helperText={
            (formik.dirty || formik.touched.description) &&
            formik.errors.description
          }
          data-qa="PP-Text-Project-Description"
          fullWidth
        />
      </StyledDialogContent>
    </Dialog>
  );
};

const StyledDialogContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
